/**
 * `?showcase=world` — grey-box world (owner: env-builder). A 40 × 24 procedural dev map with
 * collision, a water zone, a roofed shelter and three NPC stand-ins. Exercises the real WorldScene:
 * WASD/arrows walk (via the characters PlayerController), 1-5 switch weather with a timed transition,
 * P pans the camera, K shakes it, L toggles the input lock, R restarts the scene (leak round-trip),
 * Q returns to the title. Esc is NOT bound here: it belongs to the UI pause menu (and to cinematic
 * skip), so the grey box never double-handles it. The dev map is built lazily from `ctx.seed`.
 */
import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W, TILE } from '@/core/config';
import { bus, Events, type EventPayloads } from '@/core/events';
import { SceneKeys } from '@/core/scenes/keys';
import type { Facing, SceneEntryData, WeatherState } from '@/core/types';
import type { MapExit, MapInteraction } from '@/core/types/contracts';
import { MAPS } from '@/maps';
import { ACTOR_DEFS } from '@/characters';
import { SHOWCASES } from '@/content/ShowcaseScene';
import { SeededRng } from '@/core/rng';
import { WorldScene, type InteractKind, type WorldSceneConfig } from './WorldScene';
import { buildDevMap, DEV_MAP_ID, DEV_RECTS } from './dev/devMap';
import { drawDevMapLayers, ensureDevTileset } from './dev/devTextures';
import { WEATHER_ORDER } from './WeatherDirector';
import { depthForFeet } from './depth';

export const WORLD_SHOWCASE_KEY = 'WorldShowcase';
export const FONT_FAMILY = '"Nantian Sans", "Noto Sans SC", sans-serif';

export const WEATHER_LABEL: Record<WeatherState, string> = { sultry: '闷热', wind: '起风', drizzle: '细雨', rain: '大雨', 'interior-rain': '屋内听雨' };

export interface WorldDebugHandle {
  ready: boolean;
  restarts: number;
  getState(): {
    weather: WeatherState; t: number; player: { x: number; y: number; facing: string }; actors: string[]; locked: boolean;
    steps: number; camera: { x: number; y: number; mode: string }; simTimeMs: number; control: string; mapDrawn: boolean;
    prompt: string | null; lastInteract: string | null; lastExit: string | null; position: { map: string; x: number; y: number } | null;
    /** ctx seed, the seed the dev map was built with and a fingerprint of its tile layers (`?seed=` verification). */
    seed: number | null; mapSeed: number | null; mapFingerprint: number;
    /** Every non-party actor: feet position + whether it is walking (NPC weather-timing verification). */
    npcs: Record<string, { x: number; y: number; moving: boolean }>;
  };
  setWeather(state: WeatherState, ms?: number): void;
  restart(): void;
  lock(on: boolean): void;
  pan(x: number, y: number, ms: number): Promise<void>;
  follow(): void;
  shake(ms: number, intensity: number): void;
  /** Confirm press through the scene API (same path as Enter/Z/Space); returns the interaction id used. */
  interact(): string | null;
  /** Teleport the player (verification shortcut). */
  teleport(x: number, y: number, facing?: Facing): void;
}
declare global { interface Window { __nantianWorld?: WorldDebugHandle } }

/** Seed the registered dev map was built with (null until the first scene create). */
let devMapSeed: number | null = null;
/** Builds/rebuilds the dev map so it always reflects the current `ctx.seed`; idempotent per seed. */
function ensureDevMap(seed: number): void {
  if (MAPS[DEV_MAP_ID] && devMapSeed === seed) return;
  MAPS[DEV_MAP_ID] = buildDevMap(seed);
  devMapSeed = seed;
}
function mapFingerprint(layers: { data: number[] }[]): number {
  return SeededRng.hash(layers.map((l) => l.data.join(',')).join('|'));
}

export class GreyMapScene extends WorldScene {
  protected readonly mapId = DEV_MAP_ID;
  private hud: Phaser.GameObjects.Text | null = null;
  private toast: Phaser.GameObjects.Text | null = null;
  private toastLeft = 0;
  private labels = new Map<string, Phaser.GameObjects.Text>();
  private lastWeather: { state: WeatherState; t: number } = { state: 'sultry', t: 1 };
  private promptText = '';
  private lastInteract: string | null = null;
  private lastExit: string | null = null;
  private restarts = 0;
  /** keydown-* handlers this scene added to the KeyboardPlugin; removed one by one on shutdown. */
  private keyHandlers: [string, () => void][] = [];
  private readonly onWeather = (p: EventPayloads[typeof Events.WEATHER_CHANGE]): void => { this.lastWeather = p; this.refreshHud(); };
  private readonly onPrompt = (p: EventPayloads[typeof Events.INTERACT_PROMPT]): void => {
    this.promptText = p ? `${p.label ?? ''}（${p.kind ?? '?'}·${p.id ?? '?'}）` : '';
    this.refreshHud();
  };

  constructor() { super(WORLD_SHOWCASE_KEY); }

  protected override worldConfig(): WorldSceneConfig {
    return { player: { id: 'player', actor: 'jiang-zichao' }, followers: [{ id: 'zhao', actor: 'zhao-chengbo' }], defaultSpawn: 'start', weatherTransitionMs: 2500 };
  }

  protected override onPreCreate(): void {
    ensureDevMap(this.ctx?.seed ?? 1157);
    ensureDevTileset(this);
  }

  protected override onCreate(): void {
    if (!this.mapRuntimeDrew) drawDevMapLayers(this, this.map, this.overhead);
    this.drawZones();
    const actorKeys = new Map<string, string>([['player', 'jiang-zichao'], ['zhao', 'zhao-chengbo'], ...this.map.npcs.map((n) => [n.id, n.actor] as [string, string])]);
    for (const [id, actor] of this.actors) {
      const name = ACTOR_DEFS[actorKeys.get(id) ?? '']?.displayName ?? id;
      const t = this.add.text(actor.x, actor.y - 70, name, { fontFamily: FONT_FAMILY, fontSize: '11px', color: '#ffffff', stroke: '#101418', strokeThickness: 3 }).setOrigin(0.5, 1);
      this.labels.set(id, t);
    }
    this.hud = this.add.text(6, 6, '', { fontFamily: FONT_FAMILY, fontSize: '12px', color: '#e2c48c', stroke: '#101418', strokeThickness: 3, lineSpacing: 2 })
      .setScrollFactor(0).setDepth(Depth.UI);
    this.add.text(GAME_W - 6, GAME_H - 6, '[开发灰盒] 天气/相机/深度/固定步长 演示', { fontFamily: FONT_FAMILY, fontSize: '10px', color: '#c9b89a' }).setOrigin(1).setScrollFactor(0).setDepth(Depth.UI);
    this.toast = this.add.text(0, 0, '', { fontFamily: FONT_FAMILY, fontSize: '11px', color: '#ffffff', backgroundColor: '#101418', padding: { x: 4, y: 2 } })
      .setOrigin(0.5, 1).setDepth(Depth.OVERHEAD + 20).setVisible(false);
    this.lastWeather = { state: this.weather, t: 1 };
    this.promptText = '';
    this.lastInteract = null;
    this.lastExit = null;
    this.refreshHud();

    const kb = this.input.keyboard;
    if (kb) {
      const bind = (key: string, fn: () => void): void => { kb.on(`keydown-${key}`, fn); this.keyHandlers.push([`keydown-${key}`, fn]); };
      const codes = ['ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'] as const;
      codes.forEach((c, i) => bind(c, () => { const s = WEATHER_ORDER[i]; if (s) this.setWeather(s); }));
      bind('R', () => this.restartScene());
      bind('L', () => this.setInputLocked(!this.inputLocked));
      bind('P', () => { void this.demoPan(); });
      bind('K', () => this.cameraShake(500, 4));
      // Q (not Esc) leaves for the title: Esc is the pause menu's / cinematic skip's key
      bind('Q', () => { if (!this.ctx || this.ctx.router.isBusy()) return; void this.ctx.toTitle(); });
    }
    bus.onTyped(Events.WEATHER_CHANGE, this.onWeather);
    bus.onTyped(Events.INTERACT_PROMPT, this.onPrompt);

    const handle: WorldDebugHandle = {
      ready: true,
      restarts: this.restarts,
      getState: () => ({
        weather: this.weather, t: this.weatherProgress,
        player: { x: this.player.x, y: this.player.y, facing: this.player.facing },
        actors: [...this.actors.keys()], locked: this.inputLocked, steps: this.stepCount,
        camera: { x: this.cameras.main.scrollX, y: this.cameras.main.scrollY, mode: this.cameraState.mode }, simTimeMs: this.simTimeMs,
        control: this.controlMode, mapDrawn: this.mapRuntimeDrew,
        prompt: this.currentInteraction?.id ?? null, lastInteract: this.lastInteract, lastExit: this.lastExit,
        position: this.ctx?.position ? { map: this.ctx.position.map, x: this.ctx.position.x, y: this.ctx.position.y } : null,
        seed: this.ctx?.seed ?? null, mapSeed: devMapSeed, mapFingerprint: mapFingerprint(this.map.layers),
        npcs: Object.fromEntries([...this.actors].filter(([id]) => id !== 'player' && id !== 'zhao').map(([id, a]) => [id, { x: a.x, y: a.y, moving: a.isMoving() }])),
      }),
      setWeather: (s, ms) => this.setWeather(s, ms),
      restart: () => this.restartScene(),
      lock: (on) => this.setInputLocked(on),
      pan: (x, y, ms) => this.cameraPanTo(x, y, ms),
      follow: () => this.cameraFollowPlayer(),
      shake: (ms, i) => this.cameraShake(ms, i),
      interact: () => this.interact()?.id ?? null,
      teleport: (x, y, facing) => this.player.setPosition(x, y, facing),
    };
    window.__nantianWorld = handle;
  }

  protected override onInteract(kind: InteractKind, i: MapInteraction): void {
    this.lastInteract = `${kind}:${i.id}`;
    this.showToast(`${i.label ?? i.id} · ${kind}${i.dialogue ? ` → ${i.dialogue}` : ''}${i.cinematic ? ` → ${i.cinematic}` : ''}`);
  }

  protected override onExit(exit: MapExit | null, via?: MapInteraction): void {
    this.lastExit = exit?.id ?? null;
    this.showToast(`出口 ${exit?.id ?? '?'} → ${exit?.target ?? '?'} / ${exit?.spawn ?? '?'}${via ? '（门）' : ''}`);
  }

  protected override onFixedStep(dt: number): void {
    if (this.toastLeft <= 0) return;
    this.toastLeft -= dt;
    if (this.toastLeft <= 0) this.toast?.setVisible(false);
  }

  protected override onPostUpdate(): void {
    for (const [id, t] of this.labels) {
      const a = this.actors.get(id);
      if (!a) continue;
      t.setPosition(a.x, a.y - 70).setDepth(depthForFeet(a.y) + 1);
    }
    if (this.toast?.visible) this.toast.setPosition(this.player.x, this.player.y - 56);
  }

  protected override onWeatherSettled(): void { this.refreshHud(); }

  protected override onShutdown(): void {
    bus.offTyped(Events.WEATHER_CHANGE, this.onWeather);
    bus.offTyped(Events.INTERACT_PROMPT, this.onPrompt);
    const kb = this.input.keyboard;
    for (const [ev, fn] of this.keyHandlers) kb?.off(ev, fn);
    this.keyHandlers = [];
    this.labels.clear();
    this.hud = null;
    this.toast = null;
    this.toastLeft = 0;
    if (window.__nantianWorld) window.__nantianWorld.ready = false;
  }

  private showToast(text: string): void {
    if (!this.toast) return;
    this.toast.setText(text).setVisible(true);
    this.toastLeft = 1500;
  }

  private restartScene(): void {
    this.restarts++;
    this.scene.restart({ spawn: 'start' } satisfies SceneEntryData);
  }

  private async demoPan(): Promise<void> {
    const pier = this.getSpawn('pier');
    if (!pier) return;
    await this.cameraPanTo(pier.x, pier.y, 900);
    await new Promise<void>((r) => this.time.delayedCall(500, r));
    this.cameraFollowPlayer();
  }

  private drawZones(): void {
    const g = this.add.graphics().setDepth(Depth.GROUND_DETAIL + 10);
    const rect = (r: { x: number; y: number; w: number; h: number }, color: number) => { g.lineStyle(1, color, 0.9); g.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1); };
    for (const w of this.map.water ?? []) rect(w, 0x6fd3c8);
    for (const s of this.map.rainShelter ?? []) rect(s, 0xffb347);
    for (const i of this.map.interactions) {
      rect(i, 0xe2c48c);
      this.add.text(i.x + i.w / 2, i.y + i.h / 2, i.label ?? i.id, { fontFamily: FONT_FAMILY, fontSize: '10px', color: '#e2c48c', stroke: '#101418', strokeThickness: 2 }).setOrigin(0.5).setDepth(Depth.GROUND_DETAIL + 11);
    }
    for (const l of this.map.lights ?? []) { g.lineStyle(1, 0xffb347, 0.5); g.strokeCircle(l.x, l.y, l.radius); }
    const house = DEV_RECTS.house;
    this.add.text(house.x * TILE + (house.w * TILE) / 2, house.y * TILE - 6, '避雨处（屋顶为上层）', { fontFamily: FONT_FAMILY, fontSize: '10px', color: '#ffffff', stroke: '#101418', strokeThickness: 2 }).setOrigin(0.5, 1).setDepth(Depth.OVERHEAD + 10);
  }

  private refreshHud(): void {
    if (!this.hud) return;
    const { state, t } = this.lastWeather;
    const pct = t < 1 ? ` (${Math.round(t * 100)}%)` : '';
    this.hud.setText([
      `灰盒世界 · 地图 ${this.map.id} ${this.map.width}×${this.map.height} · 控制：${this.controlMode}`,
      `天气：${WEATHER_LABEL[state]}${pct}   锁定：${this.inputLocked ? '是' : '否'}   提示：${this.promptText || '—'}`,
      'WASD/方向键 移动 · Enter/Z 互动 · 1-5 切换天气 · P 镜头平移 · K 震动',
      'L 锁定输入 · R 重启场景 · Q 回标题 · Esc 暂停菜单',
    ]);
  }
}

/**
 * Registers the `world` showcase; the integrator calls this from core wiring. Idempotent. The dev map
 * itself is built on scene create from `ctx.seed`. The empty Showcase host is *replaced* (`start`, not
 * `launch`): UiRoot.worldScene() and Router.current() both take the first active non-UI scene, so a
 * host left running would be the scene the pause menu pauses and the router exits — Esc would then
 * freeze the host while the grey box keeps running underneath the modal.
 */
export function registerShowcase(): void {
  SHOWCASES['world'] = (host: Phaser.Scene) => {
    const mgr = host.scene;
    if (!mgr.get(WORLD_SHOWCASE_KEY)) mgr.add(WORLD_SHOWCASE_KEY, GreyMapScene, false);
    mgr.start(WORLD_SHOWCASE_KEY, { spawn: 'start' } satisfies SceneEntryData);
    if (mgr.get(SceneKeys.UI)) mgr.bringToTop(SceneKeys.UI);
  };
}
